/**
 * Suppress the browser's built-in HTML5 validation tooltip (the orange
 * "Please fill in this field" / "Please match the requested format" speech
 * bubble) globally and replace it with our own Lithuanian toast.
 *
 * Why a single global listener instead of touching every `<form>`?
 *
 *   The repo has 18+ `<form>` elements, none of which currently use
 *   `noValidate`. Adding `noValidate` to each would also disable the
 *   browser's *prevention of submission* on invalid inputs — meaning we'd
 *   need to add JS validation everywhere too. By listening for the native
 *   `invalid` event in the capture phase and calling `preventDefault()`,
 *   we suppress only the visual tooltip; the browser still cancels the
 *   form submission for us. One file, zero per-form changes.
 *
 * Behavior:
 *   - The first invalid input in a submit attempt focuses itself and
 *     triggers a single Lithuanian toast.
 *   - Subsequent invalid events from the same submit attempt are
 *     debounced (200ms) so the user sees one toast, not five.
 *   - Browser's English tooltip never appears.
 *   - Form submission is still blocked by the browser (we did not add
 *     `noValidate`).
 *
 * Call once during app bootstrap.
 */

const TOAST_DEBOUNCE_MS = 200;

let lastToastAt = 0;

const messageFor = (input: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement): string => {
  const v = input.validity;

  if (v.valueMissing) return 'Užpildykite privalomus laukus';
  if (v.typeMismatch) {
    if (input.type === 'email') return 'Įveskite teisingą el. pašto adresą';
    if (input.type === 'url') return 'Įveskite teisingą nuorodą';
    return 'Patikrinkite įvesto lauko formatą';
  }
  if (v.tooShort) return 'Įvedėte per trumpą reikšmę';
  if (v.tooLong) return 'Įvedėte per ilgą reikšmę';
  if (v.rangeUnderflow) return 'Reikšmė per maža';
  if (v.rangeOverflow) return 'Reikšmė per didelė';
  if (v.stepMismatch) return 'Patikrinkite įvestą reikšmę';
  if (v.patternMismatch) return 'Patikrinkite įvesto lauko formatą';

  return 'Patikrinkite užpildytus laukus';
};

const handleInvalid = (event: Event) => {
  const target = event.target as HTMLElement | null;
  if (!target) return;

  const isFormControl =
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    target instanceof HTMLSelectElement;
  if (!isFormControl) return;

  // Suppress the browser's native tooltip — but let the form's own submit
  // cancellation continue. We do NOT call stopPropagation so framework code
  // can still observe the event if it wants to.
  event.preventDefault();

  const now = Date.now();
  if (now - lastToastAt < TOAST_DEBOUNCE_MS) return;
  lastToastAt = now;

  // Focus the first offending field for accessibility — same UX the
  // browser provides natively.
  if (typeof (target as HTMLElement).focus === 'function') {
    try {
      (target as HTMLElement).focus({ preventScroll: false });
    } catch {
      // Some elements throw on programmatic focus — ignore.
    }
  }

  const toast = window.__gidasToast;
  if (toast) {
    toast(messageFor(target as HTMLInputElement), 'error');
  }
};

export const installNativeFormValidationBridge = (): void => {
  // Capture phase so we run BEFORE any field-level handler can show its
  // own UI based on the same event.
  document.addEventListener('invalid', handleInvalid, true);
};
